import React, { useEffect, useRef, useState } from "react";
import api from "../api/client";
import { Upload, Star, Trash2, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MediaGallery({ ownerType, ownerId, onCoverChange }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/media", { params: { owner_type: ownerType, owner_id: ownerId } });
      setItems(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, [ownerType, ownerId]);

  const upload = async (file, asCover = false) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image > 8 Mo");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("owner_type", ownerType);
      fd.append("owner_id", ownerId);
      fd.append("kind", asCover ? "cover" : "gallery");
      fd.append("caption", "");
      fd.append("file", file);
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(asCover ? "Image de couverture définie" : "Image ajoutée");
      await load();
      if (asCover && onCoverChange) onCoverChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    } finally {
      setUploading(false);
    }
  };

  const setCover = async (id) => {
    try {
      await api.patch(`/media/${id}`, { kind: "cover" });
      toast.success("Image définie en couverture");
      await load();
      if (onCoverChange) onCoverChange();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cette image ?")) return;
    try {
      await api.delete(`/media/${id}`);
      toast.success("Image supprimée");
      await load();
      if (onCoverChange) onCoverChange();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div data-testid={`media-gallery-${ownerType}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-[#4B5775]">{items.length} image(s) · JPG / PNG / WEBP · max 8 Mo</div>
        <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-xs font-semibold cursor-pointer disabled:opacity-60">
          <Upload className="w-3.5 h-3.5" />
          {uploading ? "Envoi…" : "Ajouter une image"}
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept="image/jpeg,image/png,image/webp"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              const asCover = items.length === 0; // first one becomes cover automatically
              await upload(f, asCover);
              e.target.value = "";
            }}
            disabled={uploading}
            data-testid="media-upload-input"
          />
        </label>
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border-2 border-dashed border-[#E2E5F1] p-10 text-center text-sm text-[#9CA3B5]">
          <ImagePlus className="w-8 h-8 mx-auto mb-2 opacity-60" />
          Ajoutez la première image (elle deviendra automatiquement la photo de couverture).
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((m) => {
            const url = `${BACKEND_URL}/api/public/media/${m.id}`;
            const isCover = m.kind === "cover";
            return (
              <div
                key={m.id}
                className={`group relative rounded-md overflow-hidden border-2 ${isCover ? "border-[#E91E63]" : "border-[#E2E5F1]"} bg-white`}
                data-testid={`media-item-${m.id}`}
              >
                <div className="aspect-[4/3] bg-[#F1F3FB] overflow-hidden">
                  <img src={url} alt={m.caption || ""} loading="lazy" className="w-full h-full object-cover" />
                </div>
                {isCover && (
                  <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#E91E63] text-white text-[9px] font-bold uppercase tracking-wider">
                    <Star className="w-2.5 h-2.5 fill-white" /> Couverture
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-end justify-center gap-1.5 p-2 opacity-0 group-hover:opacity-100">
                  {!isCover && (
                    <button
                      onClick={() => setCover(m.id)}
                      title="Définir comme couverture"
                      data-testid={`media-set-cover-${m.id}`}
                      className="p-1.5 rounded bg-white hover:bg-[#E91E63] hover:text-white text-[#0F1A3D] transition"
                    >
                      <Star className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => remove(m.id)}
                    title="Supprimer"
                    data-testid={`media-delete-${m.id}`}
                    className="p-1.5 rounded bg-white hover:bg-[#C83B3B] hover:text-white text-[#0F1A3D] transition"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
